import type { EndChProxyResponse } from '~/types/api/proxy/ch/endChProxy';
import { ApiClient } from './base/apiClient';
import type { ApiResponse } from '~/types/api/common/apiResponse';
import type { GetChProxyTargetRequest, GetChProxyTargetResponse } from '~/types/api/proxy/ch/getChProxyTarget';
import type { StartChProxyRequest, StartChProxyResponse } from '~/types/api/proxy/ch/startChProxy';
import type { StartMaxProxyRequest, StartMaxProxyResponse } from '~/types/api/proxy/max/startMaxProxy';
import type { GetMaxProxyTargetRequest, GetMaxProxyTargetResponse } from '~/types/api/proxy/max/getMaxProxyTarget';
import type { EndMaxProxyResponse } from '~/types/api/proxy/max/endMaxProxy';

const BASE_PATH = '/proxy';

/**
 * 代理ログインに関するリポジトリ
 */
export class ProxyRepository {
  private apiClient: ApiClient;

  constructor(displayId: string) {
    this.apiClient = new ApiClient(displayId);
  }

  /**
   * [sp-api-206] 代理ログインCH検索API
   *
   * @param request 代理ログイン対象ユーザの検索条件
   * @returns 代理ログイン対象のユーザ一覧
   */
  async getChProxyTargetUsers(request: GetChProxyTargetRequest): Promise<ApiResponse<GetChProxyTargetResponse>> {
    const query = new URLSearchParams();
    query.append('stationCd', request.stationCd);
    query.append('stationSubCd', request.stationSubCd);
    query.append('userCompanyCd', request.userCompanyCd);
    if (request.deliveryCompanyCd !== undefined) {
      query.append('deliveryCompanyCd', request.deliveryCompanyCd);
    }
    return await this.apiClient.get<GetChProxyTargetResponse>(BASE_PATH + '/ch/search', query);
  }

  /**
   * [sp-api-207] 代理ログインCH開始API
   *
   * @param request 代理ログイン対象のユーザ情報
   * @return 代理ログイン対象のユーザ情報
   */
  async startChProxy(request: StartChProxyRequest): Promise<ApiResponse<StartChProxyResponse>> {
    return await this.apiClient.post<StartChProxyResponse>(BASE_PATH + '/ch/start', request);
  }

  /**
   * [sp-api-208] 代理ログインCH解除API
   *
   * @returns 代理ログイン前のユーザ情報
   */
  async endChProxy(): Promise<ApiResponse<EndChProxyResponse>> {
    return await this.apiClient.post<EndChProxyResponse>(BASE_PATH + '/ch/end');
  }

  /**
   * [sp-api-209] 代理ログインMAX検索API
   *
   * @param request 代理ログイン対象ユーザの検索条件
   * @returns 代理ログイン対象のユーザ一覧
   */
  async getMaxProxyTargetUsers(request: GetMaxProxyTargetRequest): Promise<ApiResponse<GetMaxProxyTargetResponse>> {
    const query = new URLSearchParams();
    query.append('stationCd', request.stationCd);
    query.append('stationSubCd', request.stationSubCd);
    query.append('userCompanyCd', request.userCompanyCd);
    if (request.deliveryCompanyCd !== undefined) {
      query.append('deliveryCompanyCd', request.deliveryCompanyCd);
    }
    return await this.apiClient.get<GetMaxProxyTargetResponse>(BASE_PATH + '/max/search', query);
  }

  /**
   * [sp-api-210] 代理ログインMAX開始API
   *
   * @param request 代理ログイン対象のユーザ情報
   * @return 代理ログイン対象のユーザ情報
   */
  async startMaxProxy(request: StartMaxProxyRequest): Promise<ApiResponse<StartMaxProxyResponse>> {
    return await this.apiClient.post<StartMaxProxyResponse>(BASE_PATH + '/max/start', request);
  }

  /**
   * [sp-api-211] 代理ログインMAX解除API
   *
   * @returns 代理ログイン前のユーザ情報
   */
  async endMaxProxy(): Promise<ApiResponse<EndMaxProxyResponse>> {
    return await this.apiClient.post<EndMaxProxyResponse>(BASE_PATH + '/max/end');
  }
}
